import React, { useState } from "react";
import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useTranslation from "../../../../../../shared/hooks/useTranslation";
import {
    Submission,
    SubmissionsResponse,
} from "../../../../../../services/supervising/submissions/types";

interface Props {
    submissions: SubmissionsResponse | undefined;
    isLoading: boolean;
}

export default function SubmissionsView({ submissions, isLoading }: Props) {
    const { t } = useTranslation();

    if (isLoading) return null;
    if (!submissions || submissions.length === 0) {
        return (
            <p className="text-xs italic text-ink-muted">{t("unit_toolbar.submissions.empty")}</p>
        );
    }

    return (
        <ul className="space-y-3">
            {submissions.map((submission) => (
                <SubmissionRow key={submission.id} submission={submission} />
            ))}
        </ul>
    );
}

function SubmissionRow({ submission }: { submission: Submission }) {
    const { t } = useTranslation();
    const tl = (key: string) => t(`unit_toolbar.submissions.${key}`);
    const [open, setOpen] = useState(false);
    const submittedAt = new Date(submission.submittedAt).toLocaleString("cs-CZ");

    return (
        <li className="rounded-md border border-border bg-surface p-2 text-xs">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2 text-ink">
                        <StatusBadge succeeded={submission.succeeded} />
                        <span className="font-medium">{submittedAt}</span>
                    </div>
                    <dl className="grid grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-y-0.5 text-ink-muted">
                        <dt>{tl("fields.user")}</dt>
                        <dd className="text-ink">{submission.user.username ?? "—"}</dd>
                        <dt>{tl("fields.document_type")}</dt>
                        <dd className="text-ink">{submission.documentType}</dd>
                        <dt>{tl("fields.http_status")}</dt>
                        <dd className="text-ink">{submission.responseStatus}</dd>
                        {submission.accountingDocumentId && (
                            <>
                                <dt>{tl("fields.accounting_document_id")}</dt>
                                <dd className="text-ink">{submission.accountingDocumentId}</dd>
                            </>
                        )}
                    </dl>
                </div>
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="text-xs text-ink-muted underline hover:text-ink"
                >
                    {tl("toggle_details")}
                </button>
            </div>
            {open && (
                <div className="mt-2 space-y-2 border-t border-border pt-2">
                    <JsonBlock label={tl("fields.payload")} value={submission.payload} />
                    <JsonBlock label={tl("fields.response")} value={submission.responseBody} />
                </div>
            )}
        </li>
    );
}

function StatusBadge({ succeeded }: { succeeded: boolean }) {
    const { t } = useTranslation();
    const classes = succeeded ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800";
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${classes}`}
        >
            <FontAwesomeIcon icon={succeeded ? faCheck : faXmark} className="text-[9px]" />
            {t(`unit_toolbar.submissions.${succeeded ? "succeeded" : "failed"}`)}
        </span>
    );
}

function JsonBlock({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-ink-muted">
                {label}
            </div>
            <pre className="max-h-64 overflow-auto rounded-md bg-surface-2 p-2 text-[11px] text-ink">
                {value}
            </pre>
        </div>
    );
}
